import React from 'react'

import { ZariContext } from '_zari/context'
import { Portlet, Button, Form, Col, Row } from '_zari/core'

import * as EstanteriaService from 'services/estanteria.service'
import * as UbicacionService from 'services/ubicacionAlmacenamiento.service'
import * as AlmacenService from 'services/almacen.service'

import { DownloadAxiosFile } from 'utils'

import EstanteriaCard from './components/EstanteriaCard'
import MatrizEstanteria from './components/MatrizEstanteria'
import BusquedaInversa from './components/BusquedaInversa'
import ModalEstanteria from './modals/Estanteria'
import ModalCelda from './modals/Celda'
import ModalAlmacen from './modals/Almacen'

import './styles.css'

const portletBreadcrumbs = [{ name: 'Ubicaciones de Almacenamiento' }]

const ListaUbicaciones = () => {
  const __zariContext = React.useContext(ZariContext)

  const [almacenes, setAlmacenes] = React.useState([])
  const [filtroAlmacen, setFiltroAlmacen] = React.useState('')

  const [estanterias, setEstanterias] = React.useState([])
  const [estanteriaSeleccionada, setEstanteriaSeleccionada] = React.useState(null)
  const [ubicaciones, setUbicaciones] = React.useState([])

  const [mostrarModalEstanteria, setMostrarModalEstanteria] = React.useState(false)
  const [estanteriaEditar, setEstanteriaEditar] = React.useState(null)
  const [estanteriaPlantilla, setEstanteriaPlantilla] = React.useState(null)

  const [mostrarModalCelda, setMostrarModalCelda] = React.useState(false)
  const [celdaSeleccionada, setCeldaSeleccionada] = React.useState(null)

  const [mostrarModalAlmacen, setMostrarModalAlmacen] = React.useState(false)
  const onAlmacenCreadoRef = React.useRef(null)

  const [productoResaltado, setProductoResaltado] = React.useState(null)

  const cargarAlmacenes = React.useCallback(() => {
    AlmacenService.getAlmacenes_Select()
      .then(({ data }) => setAlmacenes(data || []))
      .catch(() => setAlmacenes([]))
  }, [])

  const cargarEstanterias = React.useCallback(() => {
    EstanteriaService.obtenerTodo(filtroAlmacen)
      .then(({ data }) => setEstanterias(data || []))
      .catch(() => __zariContext.snack('error', 'No se pudieron cargar las estanterías'))
  }, [__zariContext, filtroAlmacen])

  const cargarUbicaciones = React.useCallback(
    (idEstanteria) => {
      UbicacionService.obtenerxEstanteria(idEstanteria)
        .then(({ data }) => setUbicaciones(data || []))
        .catch(() => __zariContext.snack('error', 'No se pudieron cargar las ubicaciones'))
    },
    [__zariContext]
  )

  React.useEffect(() => {
    cargarAlmacenes()
  }, [cargarAlmacenes])

  React.useEffect(() => {
    cargarEstanterias()
  }, [cargarEstanterias])

  React.useEffect(() => {
    if (estanteriaSeleccionada) {
      cargarUbicaciones(estanteriaSeleccionada._id)
    }
  }, [estanteriaSeleccionada, cargarUbicaciones])

  const clickNuevaEstanteria = () => {
    setEstanteriaEditar(null)
    setEstanteriaPlantilla(null)
    setMostrarModalEstanteria(true)
  }

  const clickEditarEstanteria = (est) => {
    setEstanteriaEditar(est)
    setEstanteriaPlantilla(null)
    setMostrarModalEstanteria(true)
  }

  const clickDuplicarEstanteria = (est) => {
    setEstanteriaEditar(null)
    setEstanteriaPlantilla(est)
    setMostrarModalEstanteria(true)
  }

  const abrirModalAlmacen = (callback) => {
    onAlmacenCreadoRef.current = callback || null
    setMostrarModalAlmacen(true)
  }

  const onAlmacenCreado = async () => {
    setMostrarModalAlmacen(false)
    const idsPrev = almacenes.map((a) => a._id)
    try {
      const { data } = await AlmacenService.getAlmacenes_Select()
      const lista = data || []
      setAlmacenes(lista)
      const nuevo = lista.find((a) => !idsPrev.includes(a._id))
      if (onAlmacenCreadoRef.current) {
        onAlmacenCreadoRef.current(nuevo || null)
      }
    } finally {
      onAlmacenCreadoRef.current = null
    }
  }

  const clickEliminarEstanteria = async (est) => {
    if (!window.confirm(`¿Eliminar la estantería "${est.nombre}"?\nSe eliminarán todas las celdas asociadas.`)) {
      return
    }
    try {
      await EstanteriaService.eliminar(est._id)
      __zariContext.snack('success', 'Estantería eliminada')
      if (estanteriaSeleccionada?._id === est._id) setEstanteriaSeleccionada(null)
      cargarEstanterias()
    } catch (err) {
      __zariContext.snack('error', err?.data?.error || 'Error al eliminar')
    }
  }

  const clickAbrirEstanteria = (est) => {
    setEstanteriaSeleccionada(est)
  }

  const clickVolverGrid = () => {
    setEstanteriaSeleccionada(null)
    setUbicaciones([])
  }

  const clickCelda = (celda) => {
    setCeldaSeleccionada(celda)
    setMostrarModalCelda(true)
  }

  const onCeldaActualizada = () => {
    if (estanteriaSeleccionada) cargarUbicaciones(estanteriaSeleccionada._id)
  }

  const onSeleccionarBusquedaInversa = (resultado) => {
    const est = estanterias.find((e) => e._id === resultado._idEstanteria)
    if (!est) {
      __zariContext.snack('error', 'No se encontró la estantería del producto')
      return
    }
    setProductoResaltado(resultado._idProducto)
    setEstanteriaSeleccionada(est)
    setTimeout(() => setProductoResaltado(null), 6000)
  }

  const clickDescargarReporte = async () => {
    try {
      __zariContext.loader.open('Generando reporte de ubicaciones...')
      const { data, headers } = await UbicacionService.descargarReporte(filtroAlmacen)
      DownloadAxiosFile.downloadAsBlob(data, headers, 'Reporte_Ubicaciones.xlsx')
      __zariContext.snack('success', 'Reporte descargado')
    } catch (err) {
      __zariContext.snack('error', 'Error al descargar')
    } finally {
      __zariContext.loader.close()
    }
  }

  return (
    <>
      <Portlet>
        <Portlet.Header
          title={
            estanteriaSeleccionada
              ? `${estanteriaSeleccionada.codigo} · ${estanteriaSeleccionada.nombre}`
              : 'Ubicaciones de Almacenamiento'
          }
          breadcrumbs={portletBreadcrumbs}
          buttons={
            estanteriaSeleccionada
              ? [
                  { name: '← Volver', onClick: clickVolverGrid },
                  { name: 'Editar estantería', onClick: () => clickEditarEstanteria(estanteriaSeleccionada) }
                ]
              : [
                  { name: 'Nueva Estantería', onClick: clickNuevaEstanteria },
                  { name: 'Descargar Reporte', onClick: clickDescargarReporte }
                ]
          }
        />
        <Portlet.Body>
          {!estanteriaSeleccionada && (
            <>
              <BusquedaInversa onSeleccionar={onSeleccionarBusquedaInversa} />

              <Row className='ub-filtros'>
                <Col md={4}>
                  <Form.Group>
                    <Form.Label text='ALMACÉN' htmlFor='f-almacen' md={4} />
                    <Col md={8}>
                      <Form.Select
                        id='f-almacen'
                        value={filtroAlmacen}
                        onChange={(e) => setFiltroAlmacen(e.target.value)}
                      >
                        <option value=''>Todos</option>
                        {almacenes.map((a) => (
                          <option key={a._id} value={a._id}>
                            {a.nombre}
                          </option>
                        ))}
                      </Form.Select>
                    </Col>
                  </Form.Group>
                </Col>
              </Row>

              {estanterias.length === 0 ? (
                <div className='ub-empty'>
                  <i className='fa fa-archive fa-3x' />
                  <p>No hay estanterías registradas{filtroAlmacen ? ' en este almacén' : ''}.</p>
                  <Button onClick={clickNuevaEstanteria}>Crear primera estantería</Button>
                </div>
              ) : (
                <div className='ub-grid-estanterias'>
                  {estanterias.map((est) => (
                    <EstanteriaCard
                      key={est._id}
                      estanteria={est}
                      onAbrir={() => clickAbrirEstanteria(est)}
                      onEditar={() => clickEditarEstanteria(est)}
                      onDuplicar={() => clickDuplicarEstanteria(est)}
                      onEliminar={() => clickEliminarEstanteria(est)}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          {estanteriaSeleccionada && (
            <MatrizEstanteria
              estanteria={estanteriaSeleccionada}
              ubicaciones={ubicaciones}
              onClickCelda={clickCelda}
              onMovido={() => cargarUbicaciones(estanteriaSeleccionada._id)}
              productoResaltado={productoResaltado}
            />
          )}
        </Portlet.Body>
      </Portlet>

      {mostrarModalEstanteria && (
        <ModalEstanteria
          show={mostrarModalEstanteria}
          handleClose={() => setMostrarModalEstanteria(false)}
          estanteriaEditar={estanteriaEditar}
          estanteriaPlantilla={estanteriaPlantilla}
          almacenes={almacenes}
          onClickNuevoAlmacen={abrirModalAlmacen}
          onGuardado={() => {
            setMostrarModalEstanteria(false)
            setEstanteriaPlantilla(null)
            cargarEstanterias()
            if (estanteriaSeleccionada) {
              EstanteriaService.obtenerxId(estanteriaSeleccionada._id).then(({ data }) =>
                setEstanteriaSeleccionada(data)
              )
              cargarUbicaciones(estanteriaSeleccionada._id)
            }
          }}
        />
      )}

      {mostrarModalAlmacen && (
        <ModalAlmacen
          show={mostrarModalAlmacen}
          handleClose={() => setMostrarModalAlmacen(false)}
          onCreado={onAlmacenCreado}
        />
      )}

      {mostrarModalCelda && celdaSeleccionada && (
        <ModalCelda
          show={mostrarModalCelda}
          handleClose={() => setMostrarModalCelda(false)}
          celda={celdaSeleccionada}
          onActualizado={() => {
            onCeldaActualizada()
          }}
        />
      )}
    </>
  )
}

export default ListaUbicaciones
